/**
 * MSSQL dashboard templates (S7, remediated S7-R). SQL adapted from the same
 * DMVs the Session Browser (`electron/session-browser/providers/mssqlSessionQueries.ts`)
 * and Performance Dashboard (`electron/performance-dashboard/providers/mssqlPerformanceDashboardQueries.ts`)
 * already query in production — a DBA already trusts these views.
 */
import type { DashboardTemplate } from '../templateTypes';

export const mssqlTemplates: DashboardTemplate[] = [
  {
    id: 'mssql.sessions.longest_running',
    engine: 'mssql',
    title: 'Longest running queries',
    description: 'Active statements ordered by elapsed time — the first thing to check when the server feels slow. Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+).',
    category: 'sessions',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No long-running statements right now.',
    sql: `SELECT
  r.session_id,
  s.login_name,
  r.status,
  r.wait_type,
  r.wait_time,
  DATEDIFF(SECOND, r.start_time, GETDATE()) AS elapsed_seconds,
  SUBSTRING(st.text, (r.statement_start_offset / 2) + 1,
    CASE WHEN r.statement_end_offset = -1 THEN LEN(st.text)
         ELSE (r.statement_end_offset - r.statement_start_offset) / 2 + 1 END
  ) AS current_sql
FROM sys.dm_exec_requests r
JOIN sys.dm_exec_sessions s ON r.session_id = s.session_id
CROSS APPLY sys.dm_exec_sql_text(r.sql_handle) st
WHERE s.is_user_process = 1
  AND r.session_id <> @@SPID
  AND {{dashboard_filters}}
ORDER BY r.total_elapsed_time DESC`,
  },
  {
    id: 'mssql.sessions.blocking_chains',
    engine: 'mssql',
    title: 'Blocking chains',
    description: 'Who blocks whom, on which resource — catch a stuck chain before it cascades into a timeout storm. Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+).',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No sessions are currently blocked.',
    sql: `SELECT
  r.session_id AS blocked_session_id,
  r.blocking_session_id,
  r.wait_type,
  r.wait_time,
  r.wait_resource,
  s.login_name
FROM sys.dm_exec_requests r
JOIN sys.dm_exec_sessions s ON r.session_id = s.session_id
WHERE r.blocking_session_id > 0
  AND {{dashboard_filters}}
ORDER BY r.wait_time DESC`,
  },
  {
    id: 'mssql.sessions.blocked_request_count',
    engine: 'mssql',
    title: 'Blocked requests',
    description: 'Count of requests currently waiting on a blocking session — zero is the expected steady state; any sustained non-zero count means a chain is stuck (see Blocking chains for detail). Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+); on Azure SQL Database only sessions visible to the current connection are counted, so this can under-report.',
    category: 'sessions',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'blocked_request_count',
    expectedShape: 'scalar',
    // Bare aggregate with no GROUP BY — ALWAYS returns exactly one row
    // (M23): "no data" can't happen, only a zero or non-zero count can.
    emptyStateCopy: 'Always returns one row — zero blocked requests is healthy; a sustained non-zero count needs the Blocking chains table.',
    sql: `SELECT
  COUNT(*) AS blocked_request_count
FROM sys.dm_exec_requests
WHERE blocking_session_id > 0
  AND {{dashboard_filters}}`,
  },
  {
    id: 'mssql.performance.top_waits',
    engine: 'mssql',
    title: 'Top waits',
    description: 'Top 20 wait types by accumulated wait time, cumulative since the last restart or `DBCC SQLPERF` reset — the dominant wait type tells you what the engine is starving on. Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+).',
    category: 'performance',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No accumulated wait time yet.',
    sql: `SELECT TOP 20
  wait_type,
  wait_time_ms,
  waiting_tasks_count
FROM sys.dm_os_wait_stats
WHERE waiting_tasks_count > 0
  AND wait_type NOT IN (
    'SLEEP_TASK','BROKER_TO_FLUSH','BROKER_TASK_STOP','BROKER_EVENTHANDLER',
    'XE_DISPATCHER_WAIT','XE_TIMER_EVENT','WAITFOR','CLR_AUTO_EVENT',
    'CLR_MANUAL_EVENT','LAZYWRITER_SLEEP','CHECKPOINT_QUEUE',
    'REQUEST_FOR_DEADLOCK_SEARCH','BROKER_RECEIVE_WAITFOR',
    'DIRTY_PAGE_POLL','LOGMGR_QUEUE','SQLTRACE_INCREMENTAL_FLUSH_SLEEP',
    'SP_SERVER_DIAGNOSTICS_SLEEP','QDS_ASYNC_QUEUE',
    'HADR_FILESTREAM_IOMGR_IOCOMPLETION','BROKER_TRANSMITTER',
    'FT_IFTS_SCHEDULER_IDLE_WAIT','XE_LIVE_TARGET_TVF','ONDEMAND_TASK_QUEUE',
    'HADR_WORK_QUEUE','HADR_TIMER_TASK','HADR_CLUSAPI_CALL',
    'SOS_WORK_DISPATCHER','WAIT_XTP_HOST_WAIT'
  )
  AND {{dashboard_filters}}
ORDER BY wait_time_ms DESC`,
  },
  {
    id: 'mssql.performance.buffer_cache_hit_ratio',
    engine: 'mssql',
    title: 'Buffer cache hit ratio',
    description: 'Buffer cache efficiency, instance-wide, as a percentage — a sustained drop means memory pressure is pushing reads to disk. Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+).',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'buffer_cache_hit_ratio_pct',
    expectedShape: 'scalar',
    emptyStateCopy: 'No performance counter data available.',
    sql: `SELECT
  CAST(ratio.cntr_value AS FLOAT) * 100.0 / NULLIF(base.cntr_value, 0) AS buffer_cache_hit_ratio_pct
FROM sys.dm_os_performance_counters ratio
JOIN sys.dm_os_performance_counters base
  ON base.counter_name = 'Buffer cache hit ratio base' AND base.object_name = ratio.object_name
WHERE ratio.counter_name = 'Buffer cache hit ratio'
  AND {{dashboard_filters}}`,
  },
  {
    id: 'mssql.storage.database_sizes',
    engine: 'mssql',
    title: 'Database sizes',
    description: 'Data + log size per user database — spot the one growing faster than disk was budgeted for. Requires VIEW ANY DEFINITION.',
    category: 'storage',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No user databases found on this instance.',
    sql: `SELECT
  DB_NAME(mf.database_id) AS database_name,
  SUM(CASE WHEN mf.type = 0 THEN mf.size END) * 8.0 / 1024 AS data_mb,
  SUM(CASE WHEN mf.type = 1 THEN mf.size END) * 8.0 / 1024 AS log_mb
FROM sys.master_files mf
WHERE mf.database_id > 4
  AND {{dashboard_filters}}
GROUP BY mf.database_id
ORDER BY data_mb DESC`,
  },
  {
    id: 'mssql.storage.tempdb_usage',
    engine: 'mssql',
    title: 'TempDB usage',
    description: 'Percentage of allocated TempDB space in use right now — a spike here precedes 1105 "TempDB is full" errors. Requires VIEW DATABASE STATE on tempdb (VIEW SERVER STATE on SQL Server 2019 and earlier).',
    category: 'storage',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'tempdb_used_pct',
    expectedShape: 'scalar',
    // This is a bare aggregate with no GROUP BY — it ALWAYS returns exactly
    // one row (M23): "no data" can't happen, only a low/high percentage can.
    emptyStateCopy: 'Always returns one row — a low percentage means TempDB has headroom; watch for a sustained climb toward 100%.',
    sql: `SELECT
  CAST(SUM(total_page_count - unallocated_extent_page_count) AS FLOAT) * 100.0
    / NULLIF(SUM(total_page_count), 0) AS tempdb_used_pct
FROM tempdb.sys.dm_db_file_space_usage
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'mssql.storage.log_space_usage',
    engine: 'mssql',
    title: 'Transaction log usage',
    description: "Transaction log space used as a percentage of allocated log size, per database — a sustained climb toward 100% precedes 9002 'the transaction log is full' errors. Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+).",
    category: 'storage',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No log-usage performance counters reported by this instance.',
    sql: `SELECT
  RTRIM(instance_name) AS database_name,
  ROUND(100.0 * MAX(CASE WHEN counter_name = 'Log File(s) Used Size (KB)' THEN cntr_value END)
    / NULLIF(MAX(CASE WHEN counter_name = 'Log File(s) Size (KB)' THEN cntr_value END), 0), 1) AS log_used_pct,
  MAX(CASE WHEN counter_name = 'Log File(s) Used Size (KB)' THEN cntr_value END) AS log_used_kb,
  MAX(CASE WHEN counter_name = 'Log File(s) Size (KB)' THEN cntr_value END) AS log_size_kb
FROM sys.dm_os_performance_counters
WHERE counter_name IN ('Log File(s) Used Size (KB)', 'Log File(s) Size (KB)')
  AND instance_name <> '_Total'
  AND {{dashboard_filters}}
GROUP BY instance_name
ORDER BY log_used_pct DESC`,
  },
  {
    id: 'mssql.performance.page_life_expectancy',
    engine: 'mssql',
    title: 'Page life expectancy',
    description: 'Average seconds a data page stays in the buffer pool before eviction — a sustained drop below a few hundred seconds signals memory pressure. Requires VIEW SERVER STATE (VIEW SERVER PERFORMANCE STATE on SQL Server 2022+).',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'page_life_expectancy_sec',
    expectedShape: 'scalar',
    emptyStateCopy: 'No Page life expectancy counter reported by this instance.',
    sql: `SELECT
  cntr_value AS page_life_expectancy_sec
FROM sys.dm_os_performance_counters
WHERE object_name LIKE '%Buffer Manager%'
  AND counter_name = 'Page life expectancy'
  AND {{dashboard_filters}}`,
  },
  {
    id: 'mssql.indexes.missing_indexes',
    engine: 'mssql',
    title: 'Missing indexes',
    description: "Suggestions ranked by the optimizer's estimated impact, scoped to the current database — cheap wins the DMVs already found for you. Requires VIEW DATABASE STATE (VIEW SERVER STATE on SQL Server 2019 and earlier).",
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No missing-index suggestions right now.',
    sql: `SELECT
  d.statement AS table_name,
  d.equality_columns,
  d.inequality_columns,
  d.included_columns,
  ROUND(gs.avg_total_user_cost * gs.avg_user_impact * (gs.user_seeks + gs.user_scans), 1) AS estimated_impact
FROM sys.dm_db_missing_index_details d
JOIN sys.dm_db_missing_index_groups g ON d.index_handle = g.index_handle
JOIN sys.dm_db_missing_index_group_stats gs ON g.index_group_handle = gs.group_handle
WHERE d.database_id = DB_ID()
  AND {{dashboard_filters}}
ORDER BY estimated_impact DESC`,
  },
  {
    id: 'mssql.indexes.fragmentation',
    engine: 'mssql',
    title: 'Index fragmentation',
    description: "Rowstore indexes with over 1,000 used pages (prefiltered from sys.dm_db_partition_stats so only large indexes pay the physical-stats scan) and more than 30% fragmentation, scoped to the current database — 30% is a common heuristic for choosing REBUILD over REORGANIZE, not a Microsoft-mandated cutoff, so this tile suits a longer refresh interval than most. Requires VIEW DATABASE STATE (VIEW SERVER STATE on SQL Server 2019 and earlier).",
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No indexes over 1,000 used pages exceed the 30%-fragmentation heuristic right now.',
    sql: `SELECT
  OBJECT_SCHEMA_NAME(i.object_id) AS schema_name,
  OBJECT_NAME(i.object_id) AS table_name,
  i.name AS index_name,
  ps.partition_number,
  ROUND(ips.avg_fragmentation_in_percent, 1) AS avg_fragmentation_pct,
  ips.page_count
FROM sys.dm_db_partition_stats ps
JOIN sys.indexes i ON i.object_id = ps.object_id AND i.index_id = ps.index_id
CROSS APPLY sys.dm_db_index_physical_stats(DB_ID(), ps.object_id, ps.index_id, ps.partition_number, 'LIMITED') ips
WHERE ps.used_page_count > 1000
  AND i.type IN (1, 2)
  AND ips.avg_fragmentation_in_percent > 30
  AND {{dashboard_filters}}
ORDER BY ips.avg_fragmentation_in_percent DESC`,
  },
  {
    id: 'mssql.indexes.unused_indexes',
    engine: 'mssql',
    title: 'Unused indexes',
    description: "Nonclustered, non-unique indexes on user tables that are taking writes but have had zero reads since the last SQL Server restart or database detach/attach (either resets the DMV's counters) — an index with neither reads nor writes carries no maintenance-cost signal either way, so this is scoped to ones that are actively costing writes for no query payoff. Requires VIEW DATABASE STATE (VIEW SERVER STATE on SQL Server 2019 and earlier).",
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No indexes are taking writes with zero reads since the last restart or reattach — nothing to trim.',
    sql: `SELECT
  OBJECT_SCHEMA_NAME(i.object_id) AS schema_name,
  OBJECT_NAME(i.object_id) AS table_name,
  i.name AS index_name,
  ISNULL(us.user_seeks, 0) + ISNULL(us.user_scans, 0) + ISNULL(us.user_lookups, 0) AS user_reads,
  ISNULL(us.user_updates, 0) AS user_writes
FROM sys.indexes i
LEFT JOIN sys.dm_db_index_usage_stats us
  ON us.object_id = i.object_id AND us.index_id = i.index_id AND us.database_id = DB_ID()
WHERE i.type = 2
  AND i.is_unique = 0
  AND i.is_hypothetical = 0
  AND i.is_disabled = 0
  AND OBJECTPROPERTY(i.object_id, 'IsUserTable') = 1
  AND (us.user_seeks IS NULL OR (us.user_seeks = 0 AND us.user_scans = 0 AND us.user_lookups = 0))
  AND ISNULL(us.user_updates, 0) > 0
  AND {{dashboard_filters}}
ORDER BY user_writes DESC`,
  },
  {
    id: 'mssql.jobs.failed_agent_jobs',
    engine: 'mssql',
    title: 'Failed agent jobs',
    description: 'SQL Server Agent jobs whose LAST run failed — catch broken ETL or maintenance before someone notices stale data. Requires SQL Server Agent + msdb read access (db_datareader or SQLAgentReaderRole).',
    category: 'jobs',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No failed job runs — the agent is clean.',
    sql: `WITH last_runs AS (
  SELECT
    h.job_id,
    h.run_date,
    h.run_time,
    h.run_status,
    h.message,
    ROW_NUMBER() OVER (PARTITION BY h.job_id ORDER BY h.instance_id DESC) AS rn
  FROM msdb.dbo.sysjobhistory h
  WHERE h.step_id = 0
)
SELECT
  j.name AS job_name,
  lr.run_date,
  lr.run_time,
  lr.message
FROM msdb.dbo.sysjobs j
JOIN last_runs lr ON j.job_id = lr.job_id
WHERE lr.rn = 1
  AND lr.run_status = 0
  AND {{dashboard_filters}}
ORDER BY lr.run_date DESC, lr.run_time DESC`,
  },
  {
    id: 'mssql.backup.overdue_backups',
    engine: 'mssql',
    title: 'Overdue backups',
    description: 'Online databases with no full backup in the last 24 hours, never-backed-up ones listed first — the check you want to fail loudly, not find out about during a restore. Requires msdb read access (backupset history).',
    category: 'backup',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'Every online database has a full backup within the last 24 hours.',
    sql: `SELECT
  d.name AS database_name,
  b.last_full_backup,
  DATEDIFF(HOUR, b.last_full_backup, GETDATE()) AS hours_since_backup
FROM sys.databases d
OUTER APPLY (
  SELECT TOP 1 bs.backup_finish_date AS last_full_backup
  FROM msdb.dbo.backupset bs
  WHERE bs.database_name = d.name AND bs.type = 'D'
  ORDER BY bs.backup_finish_date DESC
) b
WHERE d.database_id > 4
  AND d.state = 0
  AND (b.last_full_backup IS NULL OR DATEDIFF(HOUR, b.last_full_backup, GETDATE()) > 24)
  AND {{dashboard_filters}}
ORDER BY CASE WHEN b.last_full_backup IS NULL THEN 0 ELSE 1 END, hours_since_backup DESC`,
  },
];
